import { randomElement } from "../../engine/random";
import { SoundId } from "../../engine/things/sounds/soundId";

const SOUNDS_ASSET_DIR = "/audio/sounds/";

// Per-file loudness adjustments, applied as a gain multiplier at playback time.
// Opt-in: any file not listed here plays at its natural volume (1.0). Use this to
// tame (or boost) an individual sound file without re-editing the asset. Keyed by
// file because one SoundId may pick a random file per instance.
const FILE_VOLUME_BOOST: Record<string, number> = {
	// "swoosh_1.ogg": 0.4,
	"snd_ext_impulseexplo.wav": 0.6,
	"87535__whiprealgood__splat.ogg": 0.85,
};

// Resolved at build time by Vite — picks up every sound file in the folder automatically.
const allSoundFiles = Object.keys(import.meta.glob("/public/audio/sounds/*.{mp3,wav,ogg}", { eager: false })).map((path) =>
	path.replace("/public/audio/sounds/", ""),
);

export class SoundLoader {
	private audioContext: AudioContext;
	private audioBufferCache = new Map<string, AudioBuffer>();
	private loadPromiseCache = new Map<string, Promise<AudioBuffer | null>>();

	constructor(audioContext: AudioContext) {
		this.audioContext = audioContext;
	}

	static getFileNameForSoundId(soundId: SoundId): string | null {
		switch (soundId) {
			case SoundId.SHOT:
				return "snd_wea_mach.wav";
			case SoundId.SHOT_SOLIDIFIER:
				return "snd_wea_spli.wav";
			case SoundId.SHOT_MISSILE:
				return "snd_wea_rock.wav";
			case SoundId.SHOT_PLASMAGUN:
				return "snd_wea_mpfb.wav";
			case SoundId.SHOT_THROW:
				return "snd_glo_swipe.wav";
			case SoundId.PICKUP_BOX:
				return "snd_glo_pickup.wav";
			case SoundId.PICKUP_GOLD:
				return randomElement([
					"336575__anthousai__coins-in-cloth-01.wav",
					"336578__anthousai__coins-in-cloth-06.wav",
					"336579__anthousai__coins-in-cloth-05.wav",
				])!;
			case SoundId.ROCK_BREAK:
				return randomElement(["rock_break_2.mp3", "rock_break_3.mp3"])!;
			case SoundId.SOLIDIFYING:
				return randomElement(["solidifier_1.mp3", "solidifier_2.mp3"])!;
			case SoundId.SOLIDIFIED_MOB_EMERGE:
				return randomElement(["solidifier_1.mp3", "solidifier_2.mp3"])!;
			case SoundId.ZOOM:
				return "zoom2.mp3";
			case SoundId.LASER_BEAM:
				return "snd_wea_plas.wav";
			case SoundId.EXPLOSION:
				return "snd_ext_impulseexplo.wav";
			case SoundId.BIG_EXPLOSION:
				return "snd_exp_rock.wav";
			case SoundId.EXPLOSION_SHOCK:
				return "snd_exp_nano.wav";
			case SoundId.HEALING_SPRAY:
				return "spray2.mp3";
			case SoundId.METAL_BANG:
				return "snd_glo_debris2.wav";
			case SoundId.BIG_CONTAINER_OPEN:
				return "unlock_1.mp3";
			case SoundId.GORE_SPLAT:
				return "splat2.mp3";
			case SoundId.FLAMETROWER_LAUNCH:
				return "flame_launch2.mp3";
			case SoundId.FLAME_BURNING:
				return "fire_burn2.mp3";
			case SoundId.LANDMINE_DEPLOY:
				return "mine_deploy.mp3";
			case SoundId.DRILL_RUNNING:
				return null;
			case SoundId.GAS_RELEASE:
				return "gas_release3.mp3";
			case SoundId.GAS_RELEASE_BOSS:
				return "gas_release2.mp3";
			case SoundId.PLAYER_HURT:
				return randomElement(["male_hurt_1.mp3", "male_hurt_2.mp3", "male_hurt_12.mp3", "male_hurt_13.mp3"])!;
			case SoundId.MOB_PHISICAL_ATTACK:
				return "punch2.mp3";
			case SoundId.SQUID_SOUND:
				return "monster_2.mp3";
			case SoundId.SQUID_DIE:
				return "monster_2_hurt.mp3";
			case SoundId.HORNED_SOUND:
				return "alien2.mp3";
			case SoundId.HORNED_DIE:
				return "alien_hurt.mp3";
			case SoundId.MANDIBLE_MOVEMENT:
				return "many_legs_walking2.mp3";
			case SoundId.TERMINAL_CONFIRM:
				return "beep_1.mp3";
			case SoundId.TERMINAL_ERROR:
				return "zapsplat_science_fiction_console_error_feedback_tone_003_28903.mp3";
			case SoundId.BLAST_DOOR_OPEN:
				return "metal_door_open.mp3";
			case SoundId.SLIDING_DOOR_OPEN:
				return "metal_door_open.mp3";
			case SoundId.DRILL_SINGLE_RUN:
				return "drill_single_2.mp3";
			case SoundId.ROBOT_SWORD_ACTIVATE:
				return "sword_draw.mp3";
			case SoundId.ROBOT_SOUND:
				return randomElement(["beep9.mp3", "snd_tur_find.wav"])!;
			case SoundId.ROBOT_POWER_ON:
				return "poweron.mp3";
			case SoundId.ROBOT_TARGET_ACQUIRED:
				return "snd_tur_find.wav";
			case SoundId.ROBOT_DIE:
				return randomElement(["poweroff.mp3", "snd_glo_debris2.wav"])!;
			case SoundId.ROBOT_BIG_TARGET_ACQUIRED:
				return "snd_tur_appear.wav";
			case SoundId.ROBOT_BIG_DIE:
				return "snd_pro_powerdown.wav";
			case SoundId.ARTILLERY_AIMING_STAGE_1:
				return "aim_beep_normal.mp3";
			case SoundId.ARTILLERY_AIMING_STAGE_2:
				return "aim_beep_constant.mp3";
			case SoundId.ARTILLERY_SIMPLE_BEEP:
				return "beep16.mp3";
			case SoundId.ROBOT_BASE_ALARM:
				return "alert.mp3";
			case SoundId.BIG_SQUID_SOUND:
				return "monster_sound_1.mp3";
			case SoundId.BIG_SQUID_DIE:
				return "monster_hurt2.mp3";
			case SoundId.CAMERA:
				return "camera.mp3";
			case SoundId.NIGHT_VISION_TOGGLE:
				return "beep3.mp3";
			case SoundId.POWER_PLANT_SWITCH_ACTIVATE:
				return "snd_glo_cracksuccess.wav";
			case SoundId.JETPACK_THRUST:
				return "snd_wea_rock.wav";
			// Sword attack variants. Lunge gets its own dedicated sample; slash and jab
			// share the three lighter swoosh variants for per-hit variation.
			case SoundId.SWORD_LUNGE:
				return "swoosh_4.ogg";
			case SoundId.SWORD_SLASH:
				return randomElement(["swoosh_1.ogg", "swoosh_2.ogg", "swoosh_3.ogg"])!;
			case SoundId.SWORD_JAB:
				return randomElement(["swoosh_1.ogg", "swoosh_2.ogg", "swoosh_3.ogg"])!;
			case SoundId.CLEAVER_ATTACK:
				return "cleaver_1.ogg";
			case SoundId.TOXIC_SLUG_DIE:
				return "splat2.mp3";
			case SoundId.TOXIC_SLUG_ATTACK:
				return "87535__whiprealgood__splat.ogg";

			default:
				return null;
		}
	}

	async loadAudioBuffer(filename: string): Promise<AudioBuffer | null> {
		const cached = this.audioBufferCache.get(filename);
		if (cached) return cached;

		const pendingPromise = this.loadPromiseCache.get(filename);
		if (pendingPromise) return pendingPromise;

		const promise = (async (): Promise<AudioBuffer | null> => {
			try {
				const response = await fetch(SOUNDS_ASSET_DIR + filename);
				const arrayBuffer = await response.arrayBuffer();
				const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
				this.audioBufferCache.set(filename, audioBuffer);
				return audioBuffer;
			} catch (e) {
				console.warn(`Failed to load sound: ${filename}`, e);
				return null;
			}
		})();

		this.loadPromiseCache.set(filename, promise);
		return promise;
	}

	/** Opt-in per-file gain multiplier; 1.0 for any file without an explicit entry. */
	static getVolumeBoostForFile(filename: string): number {
		return FILE_VOLUME_BOOST[filename] ?? 1.0;
	}

	async preloadAll(): Promise<void> {
		await Promise.all(allSoundFiles.map((f) => this.loadAudioBuffer(f)));
	}
}
